import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { MotifPinItem } from './pin-item/pin-item';
import classNames from 'classnames';
import { FormFieldContext } from '../form-field/form-field.context';
import { ReactComponent as HardwareIcClose24px } from '../../../assets/icons/navigation/ic_close_24px.svg';

let nextUniqueId;
nextUniqueId = 0;

const defaultIcons = {
  clear: <HardwareIcClose24px />
};

/**
 * @typedef Props
 * @prop {{clear: React.ReactNode}} [icons]
 * @prop {string} [id]
 * @prop {3 | 4 | 5 | 6 | 7 | 8} [length]
 * @prop {string} [value]
 * @prop {boolean} [required]
 * @prop {boolean} [focus]
 * @prop {(x: string, y: number) => void} [onComplete]
 * @prop {(x: string, y: number) => void} [onChange]
 * @prop {boolean} [disabled]
 * @prop {boolean} [disabled]
 * @prop {'number' | 'text'} [type]
 * @prop {string} [className]
 * @prop {boolean} [hideClearButton]
 * @prop {string} [clearButtonTitle]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifPinEntry = (props) => {
  const {
    icons: userIcons = {},
    id = '',
    length = 3,
    value = '',
    required = false,
    focus = false,
    onComplete = () => null,
    onChange = () => null,
    disabled = false,
    password = false,
    type = 'number',
    className = '',
    hideClearButton = false,
    clearButtonTitle = 'Clear',
    ...other
  } = props;
  const icons = {
    ...defaultIcons,
    ...userIcons
  };
  const formFieldContext = useContext(FormFieldContext);
  const [clear, setClear] = useState(false);

  const [values, setValues] = useState(
    Array(length)
      .fill('')
      .map((_, i) => value.toString()[i] || '')
  );

  const [elements] = useState([]);

  const classes = classNames({
    'motif-pin-entry': true,
    'motif-pin-entry-invalid': formFieldContext.invalid,
    [className]: true
  });

  useEffect(() => {
    formFieldContext.setRequired(required);
    formFieldContext.setDisabled(disabled);
    formFieldContext.setFormElementType('pinEntry');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-pin-entry-${nextUniqueId++}`);
  }, []);

  useEffect(() => {
    if (focus) {
      focusFirstInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusFirstInput = () => {
    elements[0].focus();
  };

  const onItemChange = (newValue, index) => {
    setClear(false);
    let currentIndex = index;

    const newValues = values.slice();
    newValues[index] = newValue;
    setValues(newValues);

    // Next focus
    if (newValue.length === 1 && index < length - 1) {
      currentIndex++;
      elements[currentIndex].focus();
    }

    // Notify parent
    const pin = newValues.join('');
    onChange(pin, currentIndex);
    if (pin.length === length) {
      onComplete(pin, currentIndex);
    }
  };

  const onBackspace = index => {
    if (index > 0) {
      elements[index - 1].focus();
    }
  };

  const handleClear = () => {
    setValues(Array(length).fill(''));
    setClear(true);
    focusFirstInput();
  };

  return (
    <div id={formFieldContext.id} className="motif-pin-entry" data-motif-pin-entry>
      {values.map((item, i) => (
        <MotifPinItem
          {...other}
          aria-label="Pin Entry"
          required={required}
          key={`motif-pin-item-${i}`}
          value={item}
          invalid={formFieldContext.invalid}
          ref={n => (elements[i] = n)}
          disabled={disabled}
          password={password}
          type={type}
          clear={clear}
          className={classes}
          onBackspace={() => onBackspace(i)}
          onChange={v => onItemChange(v, i)}
        />
      ))}
      {!hideClearButton && values.some(val => val) && (
        <div className="motif-pin-entry-clear-button-wrapper">
          <button
            className="motif-pin-entry-clear-button"
            data-testid="clear-button"
            onClick={handleClear}
            disabled={disabled}
            title={clearButtonTitle}
          >
            {icons.clear}
          </button>
        </div>
      )}
    </div>
  );
};

MotifPinEntry.propTypes = {
  icons: PropTypes.shape({
    clear: PropTypes.node
  }),
  id: PropTypes.string,
  value: PropTypes.string,
  length: PropTypes.oneOf([3, 4, 5, 6, 7, 8]),
  type: PropTypes.oneOf(['number', 'text']),
  onComplete: PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  password: PropTypes.bool,
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  className: PropTypes.string,
  hideClearButton: PropTypes.bool,
  clearButtonTitle: PropTypes.string
};

export { MotifPinEntry };
